import { useI18n } from "@expertloop/i18n/react";
import { getLocaleDisplayName } from "@expertloop/i18n";
import type { MediationTranslationView } from "@expertloop/shared";
import type { TranslationViewMode } from "./translations";

interface MediationTranslationBarProps {
	view: MediationTranslationView;
	mode: TranslationViewMode;
	onChange: (mode: TranslationViewMode) => void;
	/** Langue d'interface du membre du support, proposée comme « ma langue » quand elle diffère des deux autres. */
	uiLocale: string;
}

/**
 * Langue de consultation du dossier (LANG-3, MEDIATIONUX-3) : un seul réglage, sur une seule
 * ligne, avec une seule option choisie. Il gouverne les messages, les livraisons, les motifs et le
 * rapport IA. Toute traduction est annoncée comme indicative.
 */
export function MediationTranslationBar({ view, mode, onChange, uiLocale }: MediationTranslationBarProps) {
	const { t } = useI18n();
	const options: Array<{ value: TranslationViewMode; label: string }> = [
		{ value: "reference", label: t("adminChatDetail.translation.viewReference", { language: getLocaleDisplayName(view.referenceLocale, "en") }) },
		{ value: "original", label: t("adminChatDetail.translation.viewOriginal") },
	];
	// La langue du dossier n'est proposée que si elle diffère de la langue de référence
	if (view.dossierLocale !== view.referenceLocale) {
		options.push({ value: "dossier", label: t("adminChatDetail.translation.viewDossier", { language: getLocaleDisplayName(view.dossierLocale, "en") }) });
	}
	// La langue du support n'est proposée que si elle n'est déjà ni la référence ni celle du dossier
	if (uiLocale !== view.referenceLocale && uiLocale !== view.dossierLocale) {
		options.push({ value: "mine", label: t("adminChatDetail.translation.viewMine", { language: getLocaleDisplayName(uiLocale, "en") }) });
	}

	return (
		<div className="reading-language">
			<span className="reading-language-title">{t("adminChatDetail.translation.title")}</span>
			<div className="segmented" role="group" aria-label={t("adminChatDetail.translation.title")}>
				{options.map((option) => (
					<button
						key={option.value}
						type="button"
						className={`segmented-option${mode === option.value ? " segmented-option--selected" : ""}`}
						aria-pressed={mode === option.value}
						onClick={() => onChange(option.value)}
					>
						{option.label}
					</button>
				))}
			</div>
			<span className="translation-indicative">{t("adminChatDetail.translation.indicative")}</span>
		</div>
	);
}
